import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useEffect } from "react";
import "@/App.css";

// Layout components
import { Header, Footer } from "./components/Layout";

// Pages
import HomePage from "./pages/HomePage";
import BirdsPage from "./pages/BirdsPage";
import TermitesPage from "./pages/TermitesPage";
import PestsPage from "./pages/PestsPage";
import BirdProofingLandingPage from "./pages/BirdProofingLandingPage";

// Ripley Suburb Landing Pages
import PestControlRipleyPage from "./pages/PestControlRipleyPage";
import TermiteInspectionRipleyPage from "./pages/TermiteInspectionRipleyPage";
import SolarBirdProofingRipleyPage from "./pages/SolarBirdProofingRipleyPage";

// Area Landing Pages
import BirdProofingNorthsidePage from "./pages/BirdProofingNorthsidePage";
import BirdProofingSouthsidePage from "./pages/BirdProofingSouthsidePage";
import BirdProofingIpswichPage from "./pages/BirdProofingIpswichPage";
import PestControlIpswichPage from "./pages/PestControlIpswichPage";
import TermiteInspectionIpswichPage from "./pages/TermiteInspectionIpswichPage";
import PestControlSpringfieldPage from "./pages/PestControlSpringfieldPage";
import PestControlRedbankPlainsPage from "./pages/PestControlRedbankPlainsPage";
import DoorHangerPage from "./pages/DoorHangerPage";
import QuoteBuilderPage from "./pages/QuoteBuilderPage";
import ReferPage from "./pages/ReferPage";

// New Ipswich SEO Pages
import CockroachTreatmentIpswichPage from "./pages/CockroachTreatmentIpswichPage";
import TermiteTreatmentIpswichPage from "./pages/TermiteTreatmentIpswichPage";
import BirdControlIpswichPage from "./pages/BirdControlIpswichPage";
import EndOfLeasePestControlIpswichPage from "./pages/EndOfLeasePestControlIpswichPage";
import CheapPestControlIpswichPage from "./pages/CheapPestControlIpswichPage";
import IpswichHubPage from "./pages/IpswichHubPage";
import LockyerValleyHubPage from "./pages/LockyerValleyHubPage";
import BrisbaneSouthHubPage from "./pages/BrisbaneSouthHubPage";
import BrisbaneWestHubPage from "./pages/BrisbaneWestHubPage";
import LoganCorridorHubPage from "./pages/LoganCorridorHubPage";

// Ipswich Suburb Spoke Pages
import PestControlBrassallPage from "./pages/PestControlBrassallPage";
import PestControlBoovalPage from "./pages/PestControlBoovalPage";
import PestControlBundambaPage from "./pages/PestControlBundambaPage";
import PestControlGoodnaPage from "./pages/PestControlGoodnaPage";
import PestControlCollingwoodParkPage from "./pages/PestControlCollingwoodParkPage";
import PestControlBellbirdParkPage from "./pages/PestControlBellbirdParkPage";
import PestControlNorthIpswichPage from "./pages/PestControlNorthIpswichPage";
import PestControlEastIpswichPage from "./pages/PestControlEastIpswichPage";
import PestControlLeichhardtPage from "./pages/PestControlLeichhardtPage";
import PestControlSilkstonePage from "./pages/PestControlSilkstonePage";
import PestControlYamantoPage from "./pages/PestControlYamantoPage";
import PestControlKaraleePage from "./pages/PestControlKaraleePage";
import PestControlRaceviewPage from "./pages/PestControlRaceviewPage";
import PestControlWoodendPage from "./pages/PestControlWoodendPage";
import PestControlOneMilePage from "./pages/PestControlOneMilePage";
import PestControlSadliersCrossingPage from "./pages/PestControlSadliersCrossingPage";

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

function AppContent() {
  const location = useLocation();
  const isLandingPage = location.pathname === "/solar-bird-proofing";
  
  return (
    <div className="App min-h-screen flex flex-col">
      <Toaster position="top-center" richColors />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/birds" element={<BirdsPage />} />
        <Route path="/termites" element={<TermitesPage />} />
        <Route path="/pests" element={<PestsPage />} />
        {/* Facebook Ad Landing Page - Has own header/footer */}
        <Route path="/solar-bird-proofing" element={<BirdProofingLandingPage />} />
        {/* Ripley Suburb Landing Pages */}
        <Route path="/pest-control-ripley" element={<PestControlRipleyPage />} />
        <Route path="/termite-inspection-ripley" element={<TermiteInspectionRipleyPage />} />
        <Route path="/solar-bird-proofing-ripley" element={<SolarBirdProofingRipleyPage />} />
        {/* Area Landing Pages */}
        <Route path="/bird-proofing-northside-brisbane" element={<BirdProofingNorthsidePage />} />
        <Route path="/bird-proofing-southside-brisbane" element={<BirdProofingSouthsidePage />} />
        <Route path="/bird-proofing-ipswich" element={<BirdProofingIpswichPage />} />
        <Route path="/pest-control-ipswich" element={<PestControlIpswichPage />} />
        <Route path="/termite-inspection-ipswich" element={<TermiteInspectionIpswichPage />} />
        <Route path="/pest-control-springfield" element={<PestControlSpringfieldPage />} />
        <Route path="/pest-control-redbank-plains" element={<PestControlRedbankPlainsPage />} />
        <Route path="/flyer" element={<DoorHangerPage />} />
        <Route path="/quote-builder" element={<QuoteBuilderPage />} />
        <Route path="/refer" element={<ReferPage />} />
        {/* New Ipswich SEO Pages */}
        <Route path="/ipswich" element={<IpswichHubPage />} />
        <Route path="/cockroach-treatment-ipswich" element={<CockroachTreatmentIpswichPage />} />
        <Route path="/termite-treatment-ipswich" element={<TermiteTreatmentIpswichPage />} />
        <Route path="/bird-control-ipswich" element={<BirdControlIpswichPage />} />
        <Route path="/end-of-lease-pest-control-ipswich" element={<EndOfLeasePestControlIpswichPage />} />
        <Route path="/cheap-pest-control-ipswich" element={<CheapPestControlIpswichPage />} />
        {/* Regional Hub Pages */}
        <Route path="/lockyer-valley" element={<LockyerValleyHubPage />} />
        <Route path="/pest-control-lockyer-valley" element={<LockyerValleyHubPage />} />
        <Route path="/brisbane-south" element={<BrisbaneSouthHubPage />} />
        <Route path="/pest-control-brisbane-south" element={<BrisbaneSouthHubPage />} />
        <Route path="/brisbane-west" element={<BrisbaneWestHubPage />} />
        <Route path="/pest-control-brisbane-west" element={<BrisbaneWestHubPage />} />
        <Route path="/logan" element={<LoganCorridorHubPage />} />
        <Route path="/pest-control-logan" element={<LoganCorridorHubPage />} />
        {/* Ipswich Suburb Spoke Pages */}
        <Route path="/pest-control-brassall" element={<PestControlBrassallPage />} />
        <Route path="/pest-control-booval" element={<PestControlBoovalPage />} />
        <Route path="/pest-control-bundamba" element={<PestControlBundambaPage />} />
        <Route path="/pest-control-goodna" element={<PestControlGoodnaPage />} />
        <Route path="/pest-control-collingwood-park" element={<PestControlCollingwoodParkPage />} />
        <Route path="/pest-control-bellbird-park" element={<PestControlBellbirdParkPage />} />
        <Route path="/pest-control-north-ipswich" element={<PestControlNorthIpswichPage />} />
        <Route path="/pest-control-east-ipswich" element={<PestControlEastIpswichPage />} />
        <Route path="/pest-control-leichhardt" element={<PestControlLeichhardtPage />} />
        <Route path="/pest-control-silkstone" element={<PestControlSilkstonePage />} />
        <Route path="/pest-control-yamanto" element={<PestControlYamantoPage />} />
        <Route path="/pest-control-karalee" element={<PestControlKaraleePage />} />
        <Route path="/pest-control-raceview" element={<PestControlRaceviewPage />} />
        <Route path="/pest-control-woodend" element={<PestControlWoodendPage />} />
        <Route path="/pest-control-one-mile" element={<PestControlOneMilePage />} />
        <Route path="/pest-control-sadliers-crossing" element={<PestControlSadliersCrossingPage />} />
      </Routes>
      {!isLandingPage && <Footer />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
